/**
 * Order Detail Component
 *
 * Displays full order information including:
 * - Product info with image
 * - Order summary (for paid orders)
 * - Personal details
 * - Billing address (for paid orders)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { Button, WysiwygField } from '@uncinc/uncinc-react-kitchen-sink';
import { CoreLink } from '@uncinc/react-drupal-core';

import { fetchOrderDetail } from '../../../../../store/actions/Orders';
import type {
  OrderDetailResponse,
  OrderAddress,
} from '../../../../../types/orders';
import {
  formatPrice,
  formatDate,
  formatDutchPostalCode,
} from '../../../../../helpers/formatting';
import { isElearningOrder } from '../../../../../helpers/orders';
import { ROUTE_ACCOUNT_ORDERS } from '../../../../../config/routes';

import './style.scss';

interface OrderDetailProps {
  orderId: string;
}

/**
 * Renders the name and location lines shared by the billing and delivery
 * address blocks. Empty lines are skipped so the block stays compact.
 */
const renderAddressLines = (address: OrderAddress): React.ReactNode => (
  <>
    {address.organization && <p>{address.organization}</p>}
    {(address.first_name || address.last_name) && (
      <p>{[address.first_name, address.last_name].filter(Boolean).join(' ')}</p>
    )}
    {(address.address_line1 || address.address_line2) && (
      <p>
        {[address.address_line1, address.address_line2]
          .filter(Boolean)
          .join(' ')}
      </p>
    )}
    {(address.postal_code || address.locality) && (
      <p>
        {[formatDutchPostalCode(address.postal_code), address.locality]
          .filter(Boolean)
          .join(' ')}
      </p>
    )}
  </>
);

// =============================================================================
// Component
// =============================================================================

const OrderDetail: React.FC<OrderDetailProps> = ({ orderId }) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch = useDispatch<any>();
  const history = useHistory();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [order, setOrder] = useState<OrderDetailResponse | null>(null);

  // Fetch order detail on mount
  useEffect(() => {
    setIsLoading(true);
    setHasError(false);

    dispatch(fetchOrderDetail(orderId))
      .then((data: OrderDetailResponse) => {
        setOrder(data);
      })
      .catch(() => {
        setHasError(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [dispatch, orderId]);

  const handleBackClick = useCallback(() => {
    history.push(ROUTE_ACCOUNT_ORDERS);
  }, [history]);

  const handleLicensesClick = useCallback(() => {
    history.push(`${ROUTE_ACCOUNT_ORDERS}/${orderId}/licenses`);
  }, [history, orderId]);

  // Loading state
  if (isLoading) {
    return (
      <div className="OrderDetail OrderDetail--loading">
        <p>{t('general.loading')}</p>
      </div>
    );
  }

  // Error state
  if (hasError || !order) {
    return (
      <div className="OrderDetail OrderDetail--error">
        <p>{t('mijn_vnl.orders.fetch_error')}</p>
        <Button onClick={handleBackClick}>
          {t('mijn_vnl.orders.back_to_list')}
        </Button>
      </div>
    );
  }

  const { is_free: isFree } = order;
  // License management and invite stats only apply to e-learning orders.
  const isElearning = isElearningOrder(order.product_type);
  // Multi-product orders have no single representative product, so the page
  // is titled by the order itself instead of the first product.
  const isMultiProduct = order.items.length > 1;
  const orderTitle = isMultiProduct
    ? t('mijn_vnl.orders.order_title', { number: order.order_number })
    : order.product_title;
  const fullName = [
    order.personal_details?.first_name,
    order.personal_details?.last_name,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classNames('OrderDetail', { 'OrderDetail--free': isFree })}>
      {/* Back link */}
      <div className="OrderDetail__back">
        <CoreLink
          href={ROUTE_ACCOUNT_ORDERS}
          className="OrderDetail__back-link"
        >
          <i className="icon icon-chevron-right OrderDetail__back-icon" />
          {t('mijn_vnl.orders.back_to_list_short')}
        </CoreLink>
      </div>

      {/* Header with product info */}
      <div className="OrderDetail__header">
        <div className="OrderDetail__header-content">
          <h2 className="OrderDetail__title">{orderTitle}</h2>

          {/* Meta info row */}
          <div className="OrderDetail__meta">
            {isElearning &&
              (order.someone_else || (!isFree && order.quantity > 1)) &&
              order.invite_stats && (
                <>
                  <button
                    type="button"
                    className="OrderDetail__meta-link"
                    onClick={handleLicensesClick}
                  >
                    {t('mijn_vnl.orders.participants_invited', {
                      invited: order.invite_stats.invited,
                      total: order.invite_stats.total,
                    })}
                  </button>
                  <span className="OrderDetail__meta-separator" />
                </>
            )}
            <span className="OrderDetail__meta-date">
              {formatDate(order.order_date)}
            </span>
            <span className="OrderDetail__meta-separator" />
            <span className="OrderDetail__meta-order-number">
              #{order.order_number}
            </span>
            <span className="OrderDetail__meta-separator" />
            {!isFree && order.total_price && (
              <span className="OrderDetail__meta-price">
                {formatPrice(order.total_price)}
              </span>
            )}
          </div>
        </div>

        {/* Product image */}
        {order.product_image_url && (
          <div className="OrderDetail__header-image">
            <img
              src={order.product_image_url}
              alt={order.product_title || ''}
            />
          </div>
        )}
      </div>

      {/* Order summary section (paid orders only) */}
      {!isFree && (
        <section className="OrderDetail__section OrderDetail__section--paid-order">
          <h3 className="OrderDetail__section-title">
            {t('mijn_vnl.orders.order_summary')}
          </h3>
          <div className="OrderDetail__summary">
            <div className="OrderDetail__summary-row OrderDetail__summary-row--header-divider" />
            {order.items.map((item) => (
              <React.Fragment key={item.order_item_id}>
                <div className="OrderDetail__summary-row">
                  <span className="OrderDetail__summary-title">
                    {item.title}
                  </span>
                  <span className="OrderDetail__summary-quantity">
                    {item.quantity}x
                  </span>
                  <span className="OrderDetail__summary-price">
                    {formatPrice(item.unit_price)}
                  </span>
                </div>
                <div className="OrderDetail__summary-row OrderDetail__summary-row--divider" />
              </React.Fragment>
            ))}
            {order.subtotal && (
              <div className="OrderDetail__summary-row">
                <span className="OrderDetail__summary-label">
                  {t('mijn_vnl.orders.subtotal')}
                </span>
                <span className="OrderDetail__summary-value">
                  {formatPrice(order.subtotal)}
                </span>
              </div>
            )}
            {order.discounts?.map((discount) => (
              <div
                key={`${discount.order_item_id}-${discount.label}`}
                className="OrderDetail__summary-row OrderDetail__summary-row--discount"
              >
                <span className="OrderDetail__summary-label">
                  {discount.label}
                  <span className="OrderDetail__summary-subtitle">
                    {discount.product_title}
                  </span>
                </span>
                <span className="OrderDetail__summary-value">
                  {formatPrice(discount.amount)}
                </span>
              </div>
            ))}
            {order.shipping && (
              <div className="OrderDetail__summary-row">
                <span className="OrderDetail__summary-label">
                  {t('mijn_vnl.orders.shipping')}
                </span>
                <span className="OrderDetail__summary-value">
                  {formatPrice(order.shipping)}
                </span>
              </div>
            )}
            {order.tax && (
              <div className="OrderDetail__summary-row">
                <span className="OrderDetail__summary-label">
                  {t('mijn_vnl.orders.tax')}
                </span>
                <span className="OrderDetail__summary-value">
                  {formatPrice(order.tax)}
                </span>
              </div>
            )}
            <div className="OrderDetail__summary-row OrderDetail__summary-row--divider" />
            <div className="OrderDetail__summary-row">
              <span className="OrderDetail__summary-label">
                {t('mijn_vnl.orders.payment_method')}
              </span>
              <span className="OrderDetail__summary-value--bold">
                {order.payment_method ||
                  t('mijn_vnl.orders.payment_on_invoice')}
              </span>
            </div>
            <div className="OrderDetail__summary-row OrderDetail__summary-row--total">
              <span className="OrderDetail__summary-label">
                {t('mijn_vnl.orders.total')}
              </span>
              <span className="OrderDetail__summary-value OrderDetail__summary-value--bold">
                {formatPrice(order.total_price)}
              </span>
            </div>
            <div className="OrderDetail__summary-row OrderDetail__summary-row--footer-divider" />
          </div>
        </section>
      )}

      {/* Free order label */}
      {isFree && (
        <section className="OrderDetail__section">
          <h3 className="OrderDetail__section-title">
            {isElearning
              ? t('mijn_vnl.orders.free_elearning')
              : t('mijn_vnl.orders.free_order')}
          </h3>
          <div className="OrderDetail__summary-row OrderDetail__summary-row--footer-divider" />
        </section>
      )}

      {/* Personal details section */}
      <section className="OrderDetail__section">
        <h3 className="OrderDetail__section-title">
          {t('mijn_vnl.orders.personal_details')}
        </h3>
        <div className="OrderDetail__details">
          {fullName && <p>{fullName}</p>}
          {order.personal_details?.email && (
            <p>{order.personal_details.email}</p>
          )}
          {order.personal_details?.phone && (
            <p>{order.personal_details.phone}</p>
          )}
        </div>
      </section>

      {/* Billing address section (paid orders only) */}
      {!isFree && order.billing_address && (
        <section className="OrderDetail__section">
          <h3 className="OrderDetail__section-title">
            {t('mijn_vnl.orders.billing_address')}
          </h3>
          <div className="OrderDetail__details">
            {renderAddressLines(order.billing_address)}
            {order.billing_address.invoice_email && (
              <p>{order.billing_address.invoice_email}</p>
            )}
            {order.billing_address.reference && (
              <p>
                {t('mijn_vnl.orders.reference_label')}:{' '}
                {order.billing_address.reference}
              </p>
            )}
          </div>
        </section>
      )}

      {/* Delivery address section (paid orders that ship) */}
      {!isFree && order.delivery_address && (
        <section className="OrderDetail__section">
          <h3 className="OrderDetail__section-title">
            {t('mijn_vnl.orders.delivery_address')}
          </h3>
          <div className="OrderDetail__details">
            {renderAddressLines(order.delivery_address)}
          </div>
        </section>
      )}

      {/* Help link */}
      <div className="OrderDetail__help">
        <WysiwygField
          property="schema:text"
          html={t('mijn_vnl.orders.need_help')}
        />
      </div>
    </div>
  );
};

export default OrderDetail;
